import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const cardVariants = cva(
  "text-card-foreground transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-card border border-border/50 shadow-sm rounded-lg",
        glass: "backdrop-blur-xl bg-card/70 border border-border/40 shadow-md rounded-lg",
        squad: "card-squad",
        leaderboard: "card-leaderboard",
        tournament: "card-tournament",
        external: "card-external",
        elevated: "bg-card border border-border/30 shadow-elevated rounded-lg",
        // NEW: Esports Pro Gaming Variants
        esports: "bg-card border border-primary/30 rounded-lg shadow-card hover:border-primary/50 hover:shadow-neon",
        gaming: "bg-gradient-to-br from-card to-card/80 border-2 border-primary/40 rounded-lg shadow-lg hover:border-cyan-500/60 hover:shadow-cyan",
        stat: "bg-card/90 border border-primary/20 rounded-lg p-4 hover:border-primary/40 transition-all",
        neon: "bg-card border border-cyan-500/30 rounded-lg shadow-cyan hover:border-cyan-500/50",
        gold: "bg-card border border-gold/30 rounded-lg shadow-gold hover:border-gold/50",
        live: "bg-card border-2 border-red-500/50 rounded-lg shadow-[0_0_30px_rgba(239,68,68,0.3)] animate-pulse",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, className }))}
      {...props}
    />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-4 md:p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("font-gaming text-lg md:text-xl font-bold leading-none tracking-wide", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-4 pt-0 md:p-6 md:pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-4 pt-0 md:p-6 md:pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
